/**
 * Subnational compliance rules (US states, Spanish CCAA, Australian states,
 * Mexican states, Argentine provinces, Canadian provinces, etc.)
 *
 * Extracted from menta tech's Compliance Engine (April 2026).
 * The map renders tiers at country level only; per-state/region detail is
 * surfaced inside the country analysis panels via `getRulesForCountry()`.
 */
import type { Tier } from './tiers';

export type SubnationalRule = {
  /** Parent country ISO2 (e.g., 'US', 'ES', 'AU') */
  country: string;
  /** State/province/CCAA code (e.g., 'CA', 'MAD', 'NSW') */
  region: string;
  /** Optional city for sub-state rules (e.g., 'CDMX') */
  city?: string;
  /** Display name of the region */
  name: string;
  /** Short human-readable rule title */
  title: string;
  /** Full description (1-3 sentences max) */
  description: string;
  /** Menta's operational stance in this region */
  tier: Tier;
  /** Optional legal reference / statute */
  legalRef?: string;
  /** 'enforcement' = rule restricts; 'openMarket' = Menta-friendly */
  ruleType: 'enforcement' | 'openMarket';
};

export const SUBNATIONAL_RULES: SubnationalRule[] = [
  /* ══════════════════ UNITED STATES ══════════════════ */
  {
    country: 'US', region: 'FEDERAL', name: 'Federal (FTC)',
    title: 'FTC All-In Pricing Rule (16 CFR Part 464)',
    description: 'Effective May 12, 2025. Requires ticket sellers (including secondary) to disclose total price up front with separate government taxes. Active enforcement against Live Nation/Ticketmaster (Sept 2025).',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: '16 CFR Part 464',
  },
  {
    country: 'US', region: 'AR', name: 'Arkansas',
    title: 'Open Market (Act 590, 2023)',
    description: 'Act 590 repealed the prior resale ban entirely. Resale above face value legal for all event types. 10-day hold applies only to public-sale college sports tickets.',
    tier: 'easy', ruleType: 'openMarket',
    legalRef: 'Act 590 (HB 1650, 2023)',
  },
  {
    country: 'US', region: 'AZ', name: 'Arizona',
    title: 'Street Resale Ban + Speculative Ticketing Ban',
    description: 'A.R.S. §13-3718 bans above-face resale within 200 ft of venue (online unaffected). HB 2194 bans speculative ticketing. Bot ban. Online platforms operational.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'A.R.S. §13-3718; HB 2194',
  },
  {
    country: 'US', region: 'CA', name: 'California',
    title: 'All-In Pricing (SB 478) + Ticket Possession + Bot Ban',
    description: 'SB 478 (July 2024) mandates all-in pricing. B&P Code §22502(a) requires seller to possess ticket before listing. AB 329 (B&P §22505) prohibits bot purchases. Seat disclosure required.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'SB 478; B&P Code §§22502, 22505',
  },
  {
    country: 'US', region: 'CO', name: 'Colorado',
    title: 'Anti-Restriction Law + HB24-1378',
    description: 'Venues prohibited from restricting resale or cancelling tickets because resold. No price caps. HB24-1378 (Aug 2024) added all-in pricing mandatory at first display + refund guarantee.',
    tier: 'easy', ruleType: 'openMarket',
    legalRef: 'HB24-1378',
  },
  {
    country: 'US', region: 'DC', name: 'Washington DC',
    title: 'Pending 10% Cap (not passed)',
    description: 'A 10% cap bill was introduced but did not pass in the 2025-2026 legislative cycle. Currently open market. Monitor DC Council for new proposals.',
    tier: 'easy', ruleType: 'openMarket',
  },
  {
    country: 'US', region: 'FL', name: 'Florida',
    title: 'Conditional Online Resale',
    description: 'Fla. §817.36 imposes $1 markup cap. Internet resale exempt IF website: (1) posts refund guarantee for non-delivery/cancellation, (2) discloses reseller status, (3) meets marking requirements.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'Fla. Stat. §817.36',
  },
  {
    country: 'US', region: 'GA', name: 'Georgia',
    title: 'Broker License Required for Above-Face-Value',
    description: 'O.C.G.A. §§43-4B-25 through 43-4B-32 requires $510/year broker license for above-face-value resale. Original purchasers reselling for personal use (not for profit) exempt per §43-4B-29.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'O.C.G.A. §§43-4B-25 to 43-4B-32',
  },
  {
    country: 'US', region: 'IL', name: 'Illinois',
    title: 'Broker Registration (815 ILCS 414)',
    description: 'Illinois Ticket Sale and Resale Act requires broker registration for permanent offices or regular profit sales via internet. No fixed ticket-volume threshold. Registration includes $100K consumer protection fund.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: '815 ILCS 414',
  },
  {
    country: 'US', region: 'KY', name: 'Kentucky',
    title: 'Face Value Cap (Issuer Authorization Exemption)',
    description: 'KRS 518.070 prohibits resale above face value unless authorized by issuer or law. Classified as violation (lowest offense). Rarely enforced against online platforms. Organizer-authorized model exempt.',
    tier: 'easy', ruleType: 'openMarket',
    legalRef: 'KRS 518.070',
  },
  {
    country: 'US', region: 'LA', name: 'Louisiana',
    title: 'Internet Exemption',
    description: 'La. R.S. 4:1 prohibits physical resale above face value but explicitly exempts internet resale at any price, provided website guarantees full refund for cancellation/denied admission/counterfeit.',
    tier: 'easy', ruleType: 'openMarket',
    legalRef: 'La. R.S. 4:1 (Acts 2022, No. 55)',
  },
  {
    country: 'US', region: 'MA', name: 'Massachusetts',
    title: 'All-In Pricing + Refund + Broker License (St. 2024 c.238)',
    description: 'Face value + $2 cap. Broker license required. Service charges must be itemized. All-in pricing mandatory. Recent 2024 legislation tightened enforcement.',
    tier: 'restricted', ruleType: 'enforcement',
    legalRef: 'St. 2024 c.238',
  },
  {
    country: 'US', region: 'MD', name: 'Maryland',
    title: 'All-In Pricing + Speculative Ban + Platform Accountability',
    description: 'SB 539 (Chapter 456, July 2024) bans speculative ticketing, requires all-in pricing throughout purchase, mandates refund for cancelled/invalid/denied tickets. Platform held accountable.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'SB 539 / Chapter 456 (2024)',
  },
  {
    country: 'US', region: 'ME', name: 'Maine',
    title: '10% Resale Cap + All-In Pricing + Bot Ban (PL Ch. 354)',
    description: 'Maine PL Ch. 354 (signed June 18, 2025, effective Sept 24, 2025) caps resale at 10% above total original price paid. Also requires all-in pricing, seat disclosure, refund for cancellation.',
    tier: 'restricted', ruleType: 'enforcement',
    legalRef: 'PL Ch. 354 (2025)',
  },
  {
    country: 'US', region: 'MN', name: 'Minnesota',
    title: 'All-In Pricing + Bot Ban + Seat Disclosure (HF 1989, 2024)',
    description: 'All-in pricing required. HF 1989 (2024) added bot ban and mandatory seat/section disclosure before purchase.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'HF 1989 (2024)',
  },
  {
    country: 'US', region: 'MO', name: 'Missouri',
    title: 'State Preemption — Open Market',
    description: 'MO State Law 67.306 overrides local bans. No city can prohibit resale at any price. St. Louis/Kansas City anti-scalping ordinances void for online sales.',
    tier: 'easy', ruleType: 'openMarket',
    legalRef: 'MO Rev. Stat. 67.306',
  },
  {
    country: 'US', region: 'NC', name: 'North Carolina',
    title: 'Venue Ground Ban Only (Online Unrestricted)',
    description: 'Physical resale on venue grounds without consent prohibited. Online resale completely unrestricted — no price cap, no broker license.',
    tier: 'easy', ruleType: 'openMarket',
  },
  {
    country: 'US', region: 'NJ', name: 'New Jersey',
    title: 'Broker Registration + Conditional Price Cap',
    description: 'N.J.S.A. 56:8-26 through 56:8-38 requires broker registration for professional resellers (>30 tickets/year). Non-brokers capped at 20% above face value or $3. Internet platforms with full refund exempt.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'N.J.S.A. 56:8-26 to 56:8-38',
  },
  {
    country: 'US', region: 'NY', name: 'New York',
    title: 'Ticket Reseller License (ACA §25.13) — Article 25 sunsets July 2026',
    description: 'Arts & Cultural Affairs Law Article 25 regulates resale. Platform exemption under §25.13 if "solely facilitating" — needs evaluation. 25+ fee-disclosure lawsuits since Dec 2023.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'ACA Art. 25',
  },
  {
    country: 'US', region: 'PA', name: 'Pennsylvania',
    title: 'Conditional Cap with Internet Exemption',
    description: "PA's 25% resale cap and physical broker license requirement REPEALED in 2007. No price cap exists today. Internet platforms must post refund guarantee for cancellation/counterfeit/denied entry.",
    tier: 'easy', ruleType: 'openMarket',
  },
  {
    country: 'US', region: 'RI', name: 'Rhode Island',
    title: 'Face Value Cap (Owner Authorization Exemption)',
    description: 'R.I. Gen. Laws §5-22-26 caps resale at greater of $3 or 10% of face value. Owner/operator may authorize in writing resale at higher prices. Organizer-authorized model exempt.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'R.I. Gen. Laws §5-22-26',
  },
  {
    country: 'US', region: 'SC', name: 'South Carolina',
    title: '$1 Cap with Internet Exemption',
    description: 'SC has $1 markup limit UNLESS internet platform with Refund Guarantee. Face value = original price including fees. Refund guarantee exemption unlocks uncapped pricing.',
    tier: 'easy', ruleType: 'openMarket',
  },
  {
    country: 'US', region: 'TN', name: 'Tennessee',
    title: 'Price Transparency + Deceptive URL Ban (HB 1231, 2023)',
    description: 'HB 1231 (effective July 1, 2023) requires all-in pricing before checkout and bans deceptive URLs impersonating primary sellers. No price cap, no speculative ban.',
    tier: 'easy', ruleType: 'openMarket',
    legalRef: 'HB 1231 (2023)',
  },
  {
    country: 'US', region: 'UT', name: 'Utah',
    title: 'Open Market + Transfer Protection (SB 138, 2023)',
    description: 'No price cap, no speculative ban. SB 138 (2023) prohibits venues/promoters from cancelling or restricting valid tickets solely because transferred/resold. Deceptive URL ban.',
    tier: 'easy', ruleType: 'openMarket',
    legalRef: 'SB 138 (2023)',
  },
  {
    country: 'US', region: 'VA', name: 'Virginia',
    title: 'Open Market (Venue Property Ban Only)',
    description: 'VA Code §15.2-969 authorizes localities to prohibit scalping on venue property. Online resale unrestricted — no price cap, no state-level licensing.',
    tier: 'easy', ruleType: 'openMarket',
    legalRef: 'VA Code §15.2-969',
  },

  /* ══════════════════ SPAIN (Autonomous Communities) ══════════════════ */
  {
    country: 'ES', region: 'AN', name: 'Andalucía',
    title: 'Open Market (Declaración Responsable)',
    description: 'Decreto 155/2018 uses declaración responsable regime. No specific resale price cap in Andalucía\'s espectáculos públicos law. Business registration only.',
    tier: 'easy', ruleType: 'openMarket',
    legalRef: 'Decreto 155/2018',
  },
  {
    country: 'ES', region: 'CAT', name: 'Catalonia',
    title: 'Physical Auth Required, Online Deferred',
    description: 'Llei 11/2009 regulates physical ticket sales and requires authorization for physical resale. Online resale deferred to e-commerce law (Ley 34/2002 LSSI) — not explicitly regulated.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'Llei 11/2009 Art. 21',
  },
  {
    country: 'ES', region: 'GA', name: 'Galicia',
    title: 'Face Value Only — Reventa Prohibited',
    description: 'Ley 10/2017 Art. 18.4-5 prohibits for-profit resale of tickets in any format (paper or electronic). Defined broadly — strict face-value-only regime required.',
    tier: 'restricted', ruleType: 'enforcement',
    legalRef: 'Ley 10/2017 Art. 18.4-5',
  },
  {
    country: 'ES', region: 'MAD', name: 'Madrid',
    title: '20% Resale Surcharge Cap + Prior Authorization',
    description: 'Ley 17/1997 Art. 24.3 requires prior authorization from Comunidad de Madrid for resale. 20% surcharge cap. Authorization only granted with organizer consent.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'Ley 17/1997 Art. 24.3',
  },
  {
    country: 'ES', region: 'PV', name: 'País Vasco',
    title: 'Face Value (Authorized Surcharge Possible)',
    description: 'Ley 10/2015 Art. 17(c-d): cannot charge above published price. Reventa con recargo (markup resale) and unauthorized venta comisionada prohibited. Authorized commissioned sale possible.',
    tier: 'restricted', ruleType: 'enforcement',
    legalRef: 'Ley 10/2015 Art. 17',
  },

  /* ══════════════════ CANADA (Provinces) ══════════════════ */
  {
    country: 'CA', region: 'MB', name: 'Manitoba',
    title: 'Face Value for Government-Owned Venues',
    description: 'Manitoba Amusements Act (C.C.S.M. c. A70) restricts resale at face value for government-owned venues only. Does NOT apply to privately-owned venues.',
    tier: 'easy', ruleType: 'openMarket',
    legalRef: 'C.C.S.M. c. A70',
  },
  {
    country: 'CA', region: 'ON', name: 'Ontario',
    title: 'Bot Ban + Disclosure (Pending Face Value Ban)',
    description: 'Ticket Sales Act 2017: bot ban in force, disclosure requirements active, ticket possession required. Original 50% price cap PAUSED 2018, formally CANCELLED. Bill 94 pending — face-value cap expected before FIFA WC 2026.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'S.O. 2017 c.33 Sch. 1; Bill 94',
  },

  /* ══════════════════ MEXICO (States) ══════════════════ */
  {
    country: 'MX', region: 'JAL', name: 'Jalisco (Guadalajara/Zapopan)',
    title: 'Reventa Prohibited (Stricter Than CDMX)',
    description: 'Guadalajara Reglamento para los Espectáculos (2000, reformed 2017) and Zapopan Reglamento para Espectáculos prohibit reventa broadly — not just above-authorized-price but resale across all channels.',
    tier: 'restricted', ruleType: 'enforcement',
  },
  {
    country: 'MX', region: 'NL', name: 'Nuevo León (Monterrey)',
    title: 'No Sobreprecio (Face Value)',
    description: 'Monterrey Reglamento de Espectáculos Públicos Art. 57: "No se permitirá la venta de boletos con sobreprecio." Art. 58 sanctions organizer-seller collusion. Fines 50-200x daily minimum wage.',
    tier: 'restricted', ruleType: 'enforcement',
    legalRef: 'Reglamento Monterrey Arts. 57-58',
  },

  /* ══════════════════ ARGENTINA (CABA + Provincia) ══════════════════ */
  {
    country: 'AR', region: 'CABA', name: 'CABA (Buenos Aires City)',
    title: 'Canal Oficial Autorizado: Precio Libre',
    description: 'Art. 91 Código Contravencional CABA (Ley 5174, 2015) prohibits for-profit resale of unauthorized tickets. Key: sanctions apply to tickets NOT placed through authorized channels — organizer-authorized marketplace compliant.',
    tier: 'moderate', ruleType: 'openMarket',
    legalRef: 'Ley 5174 Art. 91',
  },
  {
    country: 'AR', region: 'PBA', name: 'Provincia de Buenos Aires',
    title: 'Reventa Autorizada Libre',
    description: 'Dec-Ley 9321/79 Art. 65 (Código de Faltas PBA) penalizes UNAUTHORIZED above-price resale. Organizer-authorized resale falls outside scope.',
    tier: 'moderate', ruleType: 'openMarket',
    legalRef: 'Dec-Ley 9321/79 Art. 65',
  },

  /* ══════════════════ AUSTRALIA (States) ══════════════════ */
  {
    country: 'AU', region: 'NSW', name: 'New South Wales',
    title: '10% Cap When T&Cs Restrict Resale',
    description: 'Fair Trading Act 1987 Part 4A: prohibits advertising resale at more than 10% above original supply cost (face + booking fees + card surcharge). Applies ONLY when organizer T&Cs explicitly restrict resale.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'Fair Trading Act 1987 Part 4A',
  },
  {
    country: 'AU', region: 'QLD', name: 'Queensland',
    title: '10% Cap at Stadiums Queensland (SQ) Venues',
    description: 'Major Sports Facilities Act 2001: 10% cap at SQ venues (Brisbane Entertainment Centre, People First Stadium, Cbus Super Stadium, etc.). Penalties increased Feb 2024.',
    tier: 'moderate', ruleType: 'enforcement',
    legalRef: 'Major Sports Facilities Act 2001',
  },
  {
    country: 'AU', region: 'SA', name: 'South Australia',
    title: '10% Cap (Broadened Scope, No Major Event Declaration Needed)',
    description: 'SA ticket scalping laws changed — events no longer need to be declared as "major events" for protections to apply. 10% cap above original price. Broader application than VIC/QLD.',
    tier: 'moderate', ruleType: 'enforcement',
  },
  {
    country: 'AU', region: 'VIC', name: 'Victoria',
    title: '10% Cap on Declared Events (Authorized Platform Exemption)',
    description: 'Major Events Act 2009 (amended 2018, 2022): 10% above face cap for ministerially declared events. s.182F(2) prohibits resale above 10% by persons "not authorised to do so by the ticketed event organiser." Authorized platform operates without the cap.',
    tier: 'easy', ruleType: 'openMarket',
    legalRef: 'Major Events Act 2009 s.182F',
  },
  {
    country: 'AU', region: 'WA', name: 'Western Australia',
    title: 'Anti-Scalping Legislation Introduced',
    description: 'WA government announced anti-scalping legislation following SA and NSW models. 10% cap expected. Status: legislation introduced to Parliament. LOW CONFIDENCE on final enacted text.',
    tier: 'moderate', ruleType: 'enforcement',
  },

  /* ══════════════════ BRAZIL (States — if any) ══════════════════ */
  /* (Brazil state-level rules to be added as published) */

  /* ══════════════════ DOMINICAN REPUBLIC ══════════════════ */
  /* (DR has 2 rules at country level, handled in DominicanRepublic.tsx) */
];

/** Return all subnational rules for a country. */
export function getRulesForCountry(iso2: string): SubnationalRule[] {
  return SUBNATIONAL_RULES.filter((r) => r.country === iso2);
}

/** Return only the Menta-friendly rules (openMarket) for a country. */
export function getOpenMarketRegionsFor(iso2: string): SubnationalRule[] {
  return SUBNATIONAL_RULES.filter(
    (r) => r.country === iso2 && r.ruleType === 'openMarket',
  );
}

/** Return only the restrictive rules (enforcement) for a country. */
export function getEnforcementRegionsFor(iso2: string): SubnationalRule[] {
  return SUBNATIONAL_RULES.filter(
    (r) => r.country === iso2 && r.ruleType === 'enforcement',
  );
}
